import React from 'react';
import PropTypes from 'prop-types';
import Slugger from 'github-slugger';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { normalizeString } from '@plone/volto/helpers/Utils/Utils';

/**
 * Destaque — o módulo dos folders da AgSUS na página: imagem, etiqueta
 * colorida em caixa alta e texto, em três composições.
 *
 *  - topo:   cartão branco com a foto em cima, centralizado na faixa azul;
 *  - lado:   foto alta de um lado e cartão branco encostado, com a etiqueta
 *            saindo da borda do cartão;
 *  - painel: imagem (marca ou ilustração) de um lado e texto branco sobre cor
 *            sólida do outro.
 *
 * A etiqueta é o título da seção (H2 ou H3) e recebe o mesmo id que o Volto dá
 * aos intertítulos de texto, para a barra de âncoras continuar achando a seção
 * quando um intertítulo vira etiqueta. O texto são blocos do Volto guardados
 * no próprio bloco (`blocks` + `blocks_layout`, como no Grid do core).
 *
 * O Edit usa este mesmo componente e troca o texto pelo formulário de blocos
 * (`texto`) e o lugar da imagem vazia por um aviso (`semImagem`).
 */

export const COMPOSICOES = ['topo', 'lado', 'painel'];
export const CORES = ['verde', 'vermelho', 'azul'];
// Que parte da foto fica à vista quando ela é recortada para caber no espaço.
export const FOCOS = ['centro', 'esquerda', 'direita', 'alto', 'baixo'];

const escolha = (valor, opcoes, padrao) =>
  opcoes.includes(valor) ? valor : padrao;

// Imagem escolhida no site: o seletor grava uma lista de objetos; aceita
// também um objeto só ou o caminho como texto.
export const urlDaImagem = (imagem, escala = 'great') => {
  const item = Array.isArray(imagem) ? imagem[0] : imagem;
  const caminho = typeof item === 'string' ? item : item?.['@id'];
  if (!caminho) return null;
  return `${flattenToAppURL(caminho).replace(/\/$/, '')}/@@images/image/${escala}`;
};

// O mesmo cálculo do TextBlockView do Volto para o id dos intertítulos.
export const idDaEtiqueta = (texto) =>
  texto ? Slugger.slug(normalizeString(texto)) : '';

const DestaqueView = ({
  data = {},
  metadata,
  properties,
  texto,
  semImagem,
}) => {
  const composicao = escolha(data.composicao, COMPOSICOES, 'topo');
  const fundo =
    data.fundo === 'azul' || data.fundo === 'nenhum'
      ? data.fundo
      : composicao === 'painel'
        ? 'nenhum'
        : 'azul';
  const lado = data.ladoImagem === 'direita' ? 'direita' : 'esquerda';
  const ajuste =
    data.ajusteImagem === 'preencher' || data.ajusteImagem === 'inteira'
      ? data.ajusteImagem
      : composicao === 'painel'
        ? 'inteira'
        : 'preencher';
  const foco = escolha(data.posicaoImagem, FOCOS, 'centro');
  const corEtiqueta = escolha(data.corEtiqueta, CORES, 'verde');
  const corPainel = escolha(data.corPainel, CORES, 'verde');
  const Nivel = data.nivel === 'h3' ? 'h3' : 'h2';
  const rotulo = (data.etiqueta || '').trim();
  const credito = (data.credito || '').trim();
  const src = urlDaImagem(data.imagem);

  const etiqueta = rotulo ? (
    <Nivel
      id={(data.ancora || '').trim() || idDaEtiqueta(rotulo) || undefined}
      className={`ag-destaque__etiqueta ag-destaque__etiqueta--${corEtiqueta}`}
    >
      <span>{rotulo}</span>
    </Nivel>
  ) : null;

  const temTexto = (data.blocks_layout?.items || []).length > 0;
  const conteudo =
    texto !== undefined ? (
      texto
    ) : temTexto ? (
      <RenderBlocks
        // A página onde o bloco está: sem isto, um bloco de dentro (a Galeria,
        // por exemplo) não sabe o caminho. Mesmo padrão da sanfona.
        metadata={metadata || properties}
        content={{ blocks: data.blocks, blocks_layout: data.blocks_layout }}
      />
    ) : null;

  const midia = src ? (
    <figure className="ag-destaque__midia">
      <img src={src} alt={data.alt || ''} loading="lazy" />
    </figure>
  ) : (
    semImagem || null
  );

  const corpo = (
    <>
      {etiqueta}
      <div className="ag-destaque__texto">{conteudo}</div>
      {credito && <p className="ag-destaque__credito">{credito}</p>}
    </>
  );

  let miolo;
  if (composicao === 'topo') {
    miolo = (
      <div className="ag-destaque__cartao">
        {midia}
        <div className="ag-destaque__corpo">{corpo}</div>
      </div>
    );
  } else if (composicao === 'lado') {
    miolo = (
      <div className="ag-destaque__grade">
        {midia}
        <div className="ag-destaque__cartao">{corpo}</div>
      </div>
    );
  } else {
    miolo = (
      <div className="ag-destaque__grade">
        {midia}
        <div
          className={`ag-destaque__painel ag-destaque__painel--${corPainel}`}
        >
          {corpo}
        </div>
      </div>
    );
  }

  const classes = [
    'ag-destaque',
    `ag-destaque--${composicao}`,
    `ag-destaque--imagem-${lado}`,
    `ag-destaque--imagem-${ajuste}`,
    `ag-destaque--foco-${foco}`,
    fundo === 'azul' && 'ag-destaque--faixa-azul',
    !midia && 'ag-destaque--sem-imagem',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes}>
      {fundo === 'azul' ? (
        <div className="ag-destaque__inner">{miolo}</div>
      ) : (
        miolo
      )}
    </section>
  );
};

DestaqueView.propTypes = {
  data: PropTypes.shape({
    composicao: PropTypes.string,
    etiqueta: PropTypes.string,
    ancora: PropTypes.string,
    nivel: PropTypes.string,
    corEtiqueta: PropTypes.string,
    imagem: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.string,
    ]),
    alt: PropTypes.string,
    credito: PropTypes.string,
    ajusteImagem: PropTypes.string,
    posicaoImagem: PropTypes.string,
    fundo: PropTypes.string,
    ladoImagem: PropTypes.string,
    corPainel: PropTypes.string,
    blocks: PropTypes.object,
    blocks_layout: PropTypes.object,
  }),
  metadata: PropTypes.object,
  properties: PropTypes.object,
  texto: PropTypes.node,
  semImagem: PropTypes.node,
};

export default DestaqueView;
